import type {
  MutationCacheNotifyEvent,
  QueryCacheNotifyEvent,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";

const MAX_ENTRIES = 100;

export type DiagnosticStatus = "pending" | "success" | "error";

export type ServerTimingMetric = {
  name: string;
  durationMs: number;
};

export type ApiRequestDiagnostic = {
  id: string;
  method: string;
  path: string;
  startedAt: number;
  durationMs: number | null;
  status: DiagnosticStatus;
  statusCode: number | null;
  profilerUrl: string | null;
  serverTiming: ServerTimingMetric[];
};

export type TanStackOperationDiagnostic = {
  id: string;
  kind: "query" | "mutation";
  key: string;
  startedAt: number;
  durationMs: number | null;
  status: DiagnosticStatus;
};

export type DevDiagnosticsSnapshot = {
  apiRequests: ApiRequestDiagnostic[];
  tanStackOperations: TanStackOperationDiagnostic[];
};

type ApiRequestCompletion = {
  status: Exclude<DiagnosticStatus, "pending">;
  statusCode: number | null;
  profilerUrl: string | null;
  serverTiming: ServerTimingMetric[];
};

type ActiveOperation = {
  id: string;
  startedAtMonotonic: number;
};

const emptySnapshot: DevDiagnosticsSnapshot = {
  apiRequests: [],
  tanStackOperations: [],
};

let snapshot = emptySnapshot;
let entrySequence = 0;
const listeners = new Set<() => void>();
const attachedClients = new WeakSet<QueryClient>();

function isEnabled(): boolean {
  return process.env.NODE_ENV === "development" && typeof window !== "undefined";
}

function monotonicNow(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function nextId(prefix: string): string {
  entrySequence += 1;

  return `${prefix}-${entrySequence}`;
}

function updateSnapshot(nextSnapshot: DevDiagnosticsSnapshot): void {
  snapshot = nextSnapshot;
  listeners.forEach((listener) => listener());
}

function prependOperation(operation: TanStackOperationDiagnostic): void {
  updateSnapshot({
    ...snapshot,
    tanStackOperations: [operation, ...snapshot.tanStackOperations].slice(0, MAX_ENTRIES),
  });
}

function finishOperation(
  id: string,
  status: Exclude<DiagnosticStatus, "pending">,
  startedAtMonotonic: number,
): void {
  if (!snapshot.tanStackOperations.some((operation) => operation.id === id)) {
    return;
  }

  updateSnapshot({
    ...snapshot,
    tanStackOperations: snapshot.tanStackOperations.map((operation) => (
      operation.id === id
        ? {
          ...operation,
          durationMs: Math.max(0, monotonicNow() - startedAtMonotonic),
          status,
        }
        : operation
    )),
  });
}

function formatQueryKey(queryKey: QueryKey): string {
  try {
    const serialized = JSON.stringify(queryKey, (_key, value: unknown) => {
      if (typeof value === "bigint") {
        return value.toString();
      }

      if (typeof value === "string" && value.length > 80) {
        return `${value.slice(0, 77)}…`;
      }

      return value;
    });

    return serialized.length > 240 ? `${serialized.slice(0, 237)}…` : serialized;
  } catch {
    return "[query]";
  }
}

function mutationLabel(event: MutationCacheNotifyEvent): string {
  if (!("mutation" in event) || !event.mutation) {
    return "[mutation]";
  }

  const mutationKey = event.mutation.options.mutationKey;

  return mutationKey
    ? formatQueryKey(mutationKey)
    : `#${event.mutation.mutationId}`;
}

export function beginApiRequest(method: string, url: string): string | null {
  if (!isEnabled()) {
    return null;
  }

  const id = nextId("http");
  const path = url.split(/[?#]/, 1)[0] || "/";
  const request: ApiRequestDiagnostic = {
    id,
    method: method.toUpperCase(),
    path,
    startedAt: Date.now(),
    durationMs: null,
    status: "pending",
    statusCode: null,
    profilerUrl: null,
    serverTiming: [],
  };

  updateSnapshot({
    ...snapshot,
    apiRequests: [request, ...snapshot.apiRequests].slice(0, MAX_ENTRIES),
  });

  return id;
}

export function finishApiRequest(
  id: string | null,
  startedAtMonotonic: number,
  completion: ApiRequestCompletion,
): void {
  if (id === null || !isEnabled()) {
    return;
  }

  updateSnapshot({
    ...snapshot,
    apiRequests: snapshot.apiRequests.map((request) => (
      request.id === id
        ? {
          ...request,
          ...completion,
          durationMs: Math.max(0, monotonicNow() - startedAtMonotonic),
        }
        : request
    )),
  });
}

export function parseServerTiming(value: string | null): ServerTimingMetric[] {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((entry): ServerTimingMetric | null => {
      const [rawName, ...parameters] = entry.trim().split(";");
      const name = rawName.trim();
      const durationParameter = parameters
        .map((parameter) => parameter.trim())
        .find((parameter) => parameter.startsWith("dur="));
      const durationMs = durationParameter
        ? Number.parseFloat(durationParameter.slice("dur=".length).replaceAll("\"", ""))
        : Number.NaN;

      if (!/^[a-zA-Z0-9_-]+$/.test(name) || !Number.isFinite(durationMs)) {
        return null;
      }

      return {
        name,
        durationMs: Math.max(0, durationMs),
      };
    })
    .filter((metric): metric is ServerTimingMetric => metric !== null);
}

export function attachTanStackDiagnostics(queryClient: QueryClient): void {
  if (!isEnabled() || attachedClients.has(queryClient)) {
    return;
  }

  attachedClients.add(queryClient);

  const activeQueries = new Map<string, ActiveOperation>();
  const activeMutations = new Map<number, ActiveOperation>();

  queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
    if (event.type !== "updated") {
      return;
    }

    if (event.action.type === "fetch") {
      const activeOperation = {
        id: nextId("query"),
        startedAtMonotonic: monotonicNow(),
      };
      activeQueries.set(event.query.queryHash, activeOperation);
      prependOperation({
        id: activeOperation.id,
        kind: "query",
        key: formatQueryKey(event.query.queryKey),
        startedAt: Date.now(),
        durationMs: null,
        status: "pending",
      });

      return;
    }

    if (event.action.type !== "success" && event.action.type !== "error") {
      return;
    }

    const activeOperation = activeQueries.get(event.query.queryHash);

    if (!activeOperation) {
      return;
    }

    activeQueries.delete(event.query.queryHash);
    finishOperation(
      activeOperation.id,
      event.action.type === "success" ? "success" : "error",
      activeOperation.startedAtMonotonic,
    );
  });

  queryClient.getMutationCache().subscribe((event: MutationCacheNotifyEvent) => {
    if (event.type !== "updated") {
      return;
    }

    const mutationId = event.mutation.mutationId;

    if (event.action.type === "pending") {
      const activeOperation = {
        id: nextId("mutation"),
        startedAtMonotonic: monotonicNow(),
      };
      activeMutations.set(mutationId, activeOperation);
      prependOperation({
        id: activeOperation.id,
        kind: "mutation",
        key: mutationLabel(event),
        startedAt: Date.now(),
        durationMs: null,
        status: "pending",
      });

      return;
    }

    if (event.action.type !== "success" && event.action.type !== "error") {
      return;
    }

    const activeOperation = activeMutations.get(mutationId);

    if (!activeOperation) {
      return;
    }

    activeMutations.delete(mutationId);
    finishOperation(
      activeOperation.id,
      event.action.type === "success" ? "success" : "error",
      activeOperation.startedAtMonotonic,
    );
  });
}

export function subscribeDevDiagnostics(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

export function getDevDiagnosticsSnapshot(): DevDiagnosticsSnapshot {
  return snapshot;
}

export function getEmptyDevDiagnosticsSnapshot(): DevDiagnosticsSnapshot {
  return emptySnapshot;
}

export function clearDevDiagnostics(): void {
  if (!isEnabled()) {
    return;
  }

  updateSnapshot(emptySnapshot);
}
